import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  IconButton,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  AccountBalance as BankIcon,
  CreditCard as CardIcon,
  Money as CashIcon,
  ArrowBack as BackIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import ReportDataGrid from "../components/ReportDataGrid";
import reportService from "../services/reportService";
import { useNavigate } from "react-router-dom";
import image from "../assets/riyal.png";
import { formatDate } from "../utils/dateUtils";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/apiService";

const PaymentCollectionReport = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isVatAdmin = currentUser?.role === "vatadmin";
  const { t, language } = useTranslation();
  const isRtl = language === "ar";
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filters, setFilters] = useState({
    paymentMethodId: "",
    customerId: "",
    startDate: new Date(new Date().getFullYear(), 0, 1), // Start of current year
    endDate: new Date(),
    tenantId: "all",
  });
  const [flattenedData, setFlattenedData] = useState([]);

  // Fetch report data
  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = {
        paymentMethodId: filters.paymentMethodId,
        startDate: filters.startDate?.toISOString().split("T")[0],
        endDate: filters.endDate?.toISOString().split("T")[0],
      };
      if (language === "ar" || language === "en") {
        params.language = language;
      }

      if (filters.customerId) {
        params.customerId = filters.customerId;
      }

      // For vatadmin: pass tenantIds when a specific company is selected
      if (isVatAdmin && filters.tenantId && filters.tenantId !== "all") {
        params.tenantIds = filters.tenantId;
      }

      const response = await reportService.getPaymentCollection(params);

      if (response.success) {
        setReportData(response.data || []);
        setTotals(response.totals);
        setPaymentMethods(response.paymentMethods || []);

        // Flatten all payments for DataGrid
        const flatPayments = [];
        response.data.forEach((group) => {
          group.invoices.forEach((payment) => {
            flatPayments.push({
              ...payment,
              paymentMethodName: group.paymentMethodName,
              paymentMethodCategory: group.category,
              paymentMethodId: group.paymentMethodId,
            });
          });
        });
        setFlattenedData(flatPayments);
      }
    } catch (error) {
      console.error("Error fetching payment collection report:", error);
      alert(t("Reports.Failed_To_Fetch_Payment_Collection"));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get("/vat-admin/my-companies", {
        params: { limit: "all" },
      });
      const records = res.data?.data ?? res.data ?? [];
      setCompanies(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
    }
  };

  // Fetch on mount
  useEffect(() => {
    if (isVatAdmin) {
      fetchCompanies();
    }
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    fetchReport();
  };

  // Handle back
  const handleBack = () => {
    navigate("/reports");
  };

  const getMethodDisplayName = (methodName, category) => {
    if (!methodName) return "-";
    const formatted = methodName.replace(/\s+/g, "_");
    if (t(`Reports.${formatted}`) !== `Reports.${formatted}`) {
      return t(`Reports.${formatted}`);
    }
    if (category) {
      const catFormatted = category.replace(/\s+/g, "_");
      if (t(`Reports.${catFormatted}`) !== `Reports.${catFormatted}`) {
        return t(`Reports.${catFormatted}`);
      }
    }
    return methodName;
  };

  // Define columns for DataGrid
  const columns = [
    {
      field: "paymentMethodName",
      headerName: t("Reports.Payment_Method"),
      flex: 1,
      minWidth: 150,
      sortable: true,
      resizable: true,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const category = params.row.paymentMethodCategory;
        const categoryColors = {
          cash: { bg: "#e8f5e9", text: "#2e7d32" },
          card: { bg: "#e3f2fd", text: "#1565c0" },
          bank_transfer: { bg: "#f3e5f5", text: "#6a1b9a" },
          mobile_wallet: { bg: "#fff3e0", text: "#e65100" },
          other: { bg: "#f5f5f5", text: "#616161" },
        };
        const color = categoryColors[category] || categoryColors.other;

        return (
          <Chip
            label={getMethodDisplayName(params.value, category)}
            size="small"
            sx={{
              bgcolor: color.bg,
              color: color.text,
              fontWeight: 600,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      },
    },
    {
      field: "invoiceNo",
      headerName: t("Reports.Invoice_No"),
      flex: 0.8,
      minWidth: 120,
      sortable: true,
      resizable: true,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "paymentDate",
      headerName: t("Reports.Payment_Date"),
      flex: 0.9,
      minWidth: 120,
      sortable: true,
      resizable: true,
      headerAlign: "left",
      align: "left",
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: "customerName",
      headerName: t("Reports.Customer"),
      flex: 1.2,
      minWidth: 180,
      sortable: true,
      resizable: true,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => params.row.customerName || "-",
    },
    ...(isVatAdmin
      ? [
          {
            field: "companyName",
            headerName: t("Common.Company_Name") || "Company Name",
            flex: 1,
            minWidth: 140,
            sortable: true,
            headerAlign: "left",
            align: "left",
          },
        ]
      : []),
    {
      field: "amount",
      headerName: t("Reports.Payment_Amount"),
      flex: 1,
      minWidth: 140,
      type: "number",
      resizable: true,
      align: "right",
      headerAlign: "right",
      cellClassName: () => "font-weight-bold",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
            width: "100%",
            fontWeight: "bold",
          }}
        >
          <img
            src={image}
            alt="Riyal"
            style={{
              width: 18,
              height: 18,
              objectFit: "contain",
            }}
          />
          <span>
            {(params.value ?? 0).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </Box>
      ),
    },
    {
      field: "reference",
      headerName: t("Reports.Reference"),
      flex: 0.9,
      minWidth: 120,
      resizable: true,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => params.row.reference || "-",
    },
    {
      field: "notes",
      headerName: t("Reports.Notes"),
      flex: 1,
      minWidth: 150,
      resizable: true,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => params.row.notes || "-",
    },
    {
      field: "paymentStatus",
      headerName: t("Reports.Invoice_Status"),
      flex: 0.8,
      minWidth: 120,
      resizable: true,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const status = params.value?.toLowerCase();
        const colors = {
          paid: { bg: "#e8f5e9", text: "#2e7d32" },
          "partially paid": { bg: "#fff3e0", text: "#f57c00" },
          pending: { bg: "#ffebee", text: "#c62828" },
        };
        const color = colors[status] || colors.pending;
        const label =
          status === "paid"
            ? t("Reports.Paid")
            : status === "partially paid" || status === "partial"
              ? t("Reports.Partially_Paid")
              : t("Reports.Pending");

        return (
          <Chip
            label={label}
            size="small"
            sx={{
              bgcolor: color.bg,
              color: color.text,
              fontWeight: 600,
              fontSize: "0.7rem",
              height: "22px",
            }}
          />
        );
      },
    },
  ];

  // Get category icon and color
  const getCategoryStyle = (category) => {
    const styles = {
      cash: { icon: <CashIcon />, color: "#2e7d32", bg: "#e8f5e9" },
      card: { icon: <CardIcon />, color: "#1565c0", bg: "#e3f2fd" },
      bank_transfer: { icon: <BankIcon />, color: "#6a1b9a", bg: "#f3e5f5" },
      mobile_wallet: { icon: <CashIcon />, color: "#e65100", bg: "#fff3e0" },
      other: { icon: <ReceiptIcon />, color: "#616161", bg: "#f5f5f5" },
    };
    return styles[category] || styles.other;
  };

  return (
    <Container
      maxWidth={false}
      sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}
    >
      {/* Header */}
      <Box sx={{ mb: 0.5, display: "flex", alignItems: "center" }}>
        <IconButton
          onClick={handleBack}
          sx={{
            mr: isRtl ? 0 : 1.5,
            ml: isRtl ? 1.5 : 0,
            bgcolor: "rgba(67, 97, 238, 0.1)",
          }}
        >
          <BackIcon sx={{ transform: isRtl ? "rotate(180deg)" : "none" }} />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight="700" gutterBottom>
            {t("Reports.Payment_Collection_Report")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Reports.Payment_Collection_Subtitle")}
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 0.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 0,
        }}
      >
        <Typography
          variant="caption"
          fontWeight="600"
          sx={{ mb: 1, display: "block", fontSize: "0.75rem" }}
        >
          {t("Reports.Filters")}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={1} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
              <TextField
                fullWidth
                select
                label={t("Reports.Payment_Method")}
                value={filters.paymentMethodId}
                onChange={(e) =>
                  handleFilterChange("paymentMethodId", e.target.value)
                }
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 0,
                    height: "36px",
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "8px 14px",
                  },
                }}
              >
                <MenuItem value="">{t("Reports.All_Payment_Methods")}</MenuItem>
                {paymentMethods.map((method) => (
                  <MenuItem key={method._id} value={method._id}>
                    {getMethodDisplayName(method.name, method.category)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
              <DatePicker
                label={t("Reports.Start_Date")}
                value={filters.startDate}
                onChange={(date) => handleFilterChange("startDate", date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0,
                        height: "36px",
                        fontSize: "0.875rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "8px 14px",
                      },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
              <DatePicker
                label={t("Reports.End_Date")}
                value={filters.endDate}
                onChange={(date) => handleFilterChange("endDate", date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0,
                        height: "36px",
                        fontSize: "0.875rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "8px 14px",
                      },
                    },
                  },
                }}
              />
            </Grid>
            {isVatAdmin && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label={t("Common.Company_Name") || "Company"}
                  value={filters.tenantId}
                  onChange={(e) =>
                    handleFilterChange("tenantId", e.target.value)
                  }
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                      height: "36px",
                      fontSize: "0.875rem",
                    },
                    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                    "& .MuiOutlinedInput-input": { padding: "8px 14px" },
                  }}
                >
                  <MenuItem value="all">
                    {t("Common.All_Companies") || "All Companies"}
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.tenantId} value={company.tenantId}>
                      {company.name || company.tenantId}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={isVatAdmin ? 3 : 3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
                sx={{
                  borderRadius: 0,
                  height: "36px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  t("Reports.Apply_Filters")
                )}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {/* Payment Mode Totals */}
      {reportData.length > 0 && (
        <Grid container spacing={1} sx={{ mb: 0.5 }}>
          {reportData.map((paymentGroup, index) => {
            const categoryStyle = getCategoryStyle(paymentGroup.category);
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 0,
                    borderLeft: isRtl
                      ? "none"
                      : `4px solid ${categoryStyle.color}`,
                    borderRight: isRtl
                      ? `4px solid ${categoryStyle.color}`
                      : "none",
                    bgcolor: categoryStyle.bg,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "6px",
                        bgcolor: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: isRtl ? 0 : 1.5,
                        ml: isRtl ? 1.5 : 0,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      }}
                    >
                      {React.cloneElement(categoryStyle.icon, {
                        sx: { color: categoryStyle.color, fontSize: 20 },
                      })}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight="700"
                        sx={{
                          color: categoryStyle.color,
                          fontSize: "0.875rem",
                          mb: 0.25,
                        }}
                      >
                        {getMethodDisplayName(
                          paymentGroup.paymentMethodName,
                          paymentGroup.category,
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.7rem" }}
                      >
                        {paymentGroup.transactionCount}{" "}
                        {paymentGroup.transactionCount === 1
                          ? t("Reports.Transaction")
                          : t("Reports.Transactions")}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ bgcolor: "white", p: 1, borderRadius: "4px" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ fontSize: "0.7rem" }}
                    >
                      {t("Reports.Total_Collected")}
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      sx={{
                        color: categoryStyle.color,
                        fontSize: "1.1rem",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <img
                        src={image}
                        alt=""
                        style={{
                          width: "20px",
                          height: "20px",
                          objectFit: "contain",
                        }}
                      />
                      <span>
                        {paymentGroup.paidAmount.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Payment Collections DataGrid */}
      <ReportDataGrid
        title={t("Reports.Payment_Collection_Report")}
        data={flattenedData}
        columns={columns}
        loading={loading}
        onRefresh={fetchReport}
        exportFileName={`payment-collection-${new Date().toISOString().split("T")[0]}`}
        pageSize={25}
        height={700}
        getRowId={(row) =>
          row.paymentId || `${row.invoiceNo}-${row.paymentDate}`
        }
        totals={totals}
        dateRange={{ startDate: filters.startDate, endDate: filters.endDate }}
      />
    </Container>
  );
};

export default PaymentCollectionReport;
